
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { toast } from '@/hooks/errors/use-toast';

export interface UnifiedProfileData {
  firstName: string;
  lastName: string;
  profilePictureUrl: string;
  email: string;
  userRole: string;
  createdAt?: string;
}

/**
 * Unified profile data hook that consolidates all profile-related queries
 * Implements proper error handling, retries, and request deduplication
 */
export const useUnifiedProfileData = () => {
  const { user } = useUser();
  
  // Set up event listener for profile picture updates
  React.useEffect(() => {
    const handleProfilePictureUpdate = (event: CustomEvent) => {
      console.log('[Unified Profile] Detected profile picture update event:', event.detail);
      // Force refetch of profile data when profile picture is updated
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['unified-profile', user.id] });
      }
    };
    
    // Add event listeners for both profile picture update events
    document.addEventListener('profile-picture-updated', handleProfilePictureUpdate as EventListener);
    document.addEventListener('profile-picture-direct-update', handleProfilePictureUpdate as EventListener);
    
    return () => {
      // Clean up event listeners on unmount
      document.removeEventListener('profile-picture-updated', handleProfilePictureUpdate as EventListener);
      document.removeEventListener('profile-picture-direct-update', handleProfilePictureUpdate as EventListener);
    };
  }, [user?.id]);
  
  // Create a query client instance for cache operations
  const queryClient = useQueryClient();
  
  const { data: profileData = {} as UnifiedProfileData, isLoading, error, refetch } = useQuery<UnifiedProfileData>({
    queryKey: ['unified-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('No authenticated user');
      }
      
      console.log('[Unified Profile] Fetching profile data for user:', user.id);
      
      try {
        // Get user profile data from users table
        const { data: userData, error: profileError } = await supabase
          .from('users')
          .select('first_name, last_name, profile_picture_url, email, created_at')
          .eq('id', user.id)
          .single();
          
        if (profileError) {
          console.warn('[Unified Profile] Profile fetch error:', profileError.message);
          // Return fallback data with email from auth instead of throwing
          return {
            firstName: '',
            lastName: '',
            profilePictureUrl: '',
            email: user?.email || '',
            userRole: 'User',
            createdAt: ''
          } as UnifiedProfileData;
        }
        
        console.log('[Unified Profile] Successfully fetched profile data with picture URL:', userData?.profile_picture_url);
        
        return {
          firstName: userData?.first_name || '',
          lastName: userData?.last_name || '',
          profilePictureUrl: userData?.profile_picture_url || '',
          email: userData?.email || user.email || '',
          userRole: 'User', // Default role since workspace context is removed
          createdAt: userData?.created_at || ''
        };
      } catch (error) {
        console.error('[Unified Profile] Error fetching profile data:', error);
        // Return fallback data instead of throwing to prevent cascading failures
        return {
          firstName: '',
          lastName: '',
          profilePictureUrl: '',
          email: user?.email || '',
          userRole: 'User',
          createdAt: ''
        } as UnifiedProfileData;
      }
    },
    enabled: !!user?.id,
    staleTime: 0, // Set to 0 to always fetch fresh data for profile picture updates
    gcTime: 2 * 60 * 1000, // Reduced from 10 minutes to 2 minutes
    refetchOnWindowFocus: true, // Enable refetch on focus for profile picture updates
    refetchOnMount: true, // Always refetch on mount for fresh profile data
    refetchOnReconnect: true, // Refetch on network reconnection
    retry: (failureCount, error) => {
      // Don't retry if it's a network resource error
      if (error.message.includes('ERR_INSUFFICIENT_RESOURCES') || 
          error.message.includes('Failed to fetch')) {
        console.warn('[Unified Profile] Network error, not retrying:', error.message);
        return false;
      }
      // Only retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Only show toast for genuine errors, not network issues
  if (error && !error.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
    console.error('[Unified Profile] Profile data error:', error);
  }

  return { 
    profileData, 
    isLoading, 
    error,
    refetch
  };
};
